import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { Loader2, Plus, Pencil, Trash2, Package, Users, DollarSign, ShoppingBag, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", price: "", compare_price: "", category: "mesh-shorts",
    gender: "men", sizes: "S,M,L,XL,XXL", colors: "Black:#0A0A0A", features: "",
    is_featured: false, is_new: false, stock: "100",
  });

  useEffect(() => {
    if (!authLoading && (!user || user === false || user.role !== "admin")) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === "products") {
        const { data } = await api.get("/products");
        setProducts(data);
      } else if (tab === "orders") {
        const { data } = await api.get("/admin/orders");
        setOrders(data);
      } else if (tab === "stats") {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn('Admin load:', err.message); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ name: "", description: "", price: "", compare_price: "", category: "mesh-shorts",
      gender: "men", sizes: "S,M,L,XL,XXL", colors: "Black:#0A0A0A", features: "",
      is_featured: false, is_new: false, stock: "100" });
    setEditingProduct(null);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name, description: product.description, price: String(product.price),
      compare_price: product.compare_price ? String(product.compare_price) : "",
      category: product.category, gender: product.gender,
      sizes: product.sizes?.join(",") || "", colors: product.colors?.map(c => `${c.name}:${c.hex}`).join(",") || "",
      features: product.features?.join("\n") || "", is_featured: product.is_featured,
      is_new: product.is_new, stock: String(product.stock),
    });
    setShowProductModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, price: parseFloat(form.price),
      compare_price: form.compare_price ? parseFloat(form.compare_price) : null,
      category: form.category, gender: form.gender,
      sizes: form.sizes.split(",").map(s => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map(c => { const [n, h] = c.split(":"); return { name: n?.trim(), hex: h?.trim() }; }).filter(c => c.name && c.hex),
      features: form.features.split("\n").map(f => f.trim()).filter(Boolean),
      is_featured: form.is_featured, is_new: form.is_new, stock: parseInt(form.stock) || 0,
      images: editingProduct?.images || [],
    };
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
      } else {
        await api.post("/products", payload);
      }
      setShowProductModal(false);
      resetForm();
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save product");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      loadData();
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn('Delete failed:', err.message); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      loadData();
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn('Status update failed:', err.message); }
  };

  if (authLoading || !user || user.role !== "admin") {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 size={24} className="animate-spin text-gray-400" /></div>;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen">
      <SEO title="Admin | AERWEAR" description="AERWEAR account and order tools." noindex />
      <div className="border-b border-[#0A0A0A] py-8 px-6">
        <h1 className="text-3xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk']">Admin Panel</h1>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-0 border-b border-gray-200 mb-8">
          {[
            { key: "stats", label: "Dashboard", icon: <DollarSign size={14} /> },
            { key: "products", label: "Products", icon: <ShoppingBag size={14} /> },
            { key: "orders", label: "Orders", icon: <Package size={14} /> },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] border-b-2 flex items-center gap-2 transition-colors ${
                tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-gray-400"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-gray-400" /></div>}

        {/* Stats Tab */}
        {!loading && tab === "stats" && stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: "Products", value: stats.total_products, icon: <ShoppingBag size={20} /> },
              { label: "Orders", value: stats.total_orders, icon: <Package size={20} /> },
              { label: "Users", value: stats.total_users, icon: <Users size={20} /> },
              { label: "Revenue", value: `$${stats.total_revenue?.toFixed(2)}`, icon: <DollarSign size={20} /> },
              { label: "Subscribers", value: stats.total_subscribers, icon: <Mail size={20} /> },
            ].map((s) => (
              <div key={s.label} className="border border-gray-200 p-6 text-center hover:border-[#0A0A0A] transition-colors">
                <div className="flex justify-center mb-3 text-gray-400">{s.icon}</div>
                <p className="text-2xl font-bold font-['Outfit']">{s.value}</p>
                <p className="text-xs uppercase tracking-wider text-gray-400 font-['Outfit'] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Products Tab */}
        {!loading && tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-6">
              <p className="text-xs text-gray-400 uppercase tracking-widest font-['Outfit']">{products.length} products</p>
              <button
                data-testid="add-product-btn"
                onClick={() => { resetForm(); setShowProductModal(true); }}
                className="flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#1a1a1a] transition-colors"
              >
                <Plus size={14} /> Add Product
              </button>
            </div>
            <div className="border border-gray-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-[#F9FAFB]">
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold font-['Outfit'] text-gray-500">Product</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold font-['Outfit'] text-gray-500 hidden md:table-cell">Gender</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold font-['Outfit'] text-gray-500">Price</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold font-['Outfit'] text-gray-500 hidden md:table-cell">Stock</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold font-['Outfit'] text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold font-['Outfit']">{p.name}</p>
                        <div className="flex gap-1 mt-1">
                          {p.is_featured && <span className="text-[10px] bg-gray-100 px-2 py-0.5 uppercase tracking-wider">Featured</span>}
                          {p.is_new && <span className="text-[10px] bg-[#0A0A0A] text-white px-2 py-0.5 uppercase tracking-wider">New</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-['Outfit'] capitalize hidden md:table-cell">{p.gender}</td>
                      <td className="px-4 py-3 text-sm font-semibold font-['Outfit']">${p.price?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm font-['Outfit'] hidden md:table-cell">{p.stock}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-gray-100 transition-colors"><Pencil size={14} /></button>
                          <button data-testid={`delete-product-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 text-red-500 transition-colors"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {!loading && tab === "orders" && (
          <div data-testid="admin-orders">
            <p className="text-xs text-gray-400 uppercase tracking-widest font-['Outfit'] mb-6">{orders.length} orders</p>
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="border border-gray-200 p-6 hover:border-[#0A0A0A] transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-400 font-['Outfit']">#{order.id?.slice(0, 8)} &middot; {order.user_email}</p>
                      <p className="text-sm font-semibold font-['Outfit'] mt-1">${order.total?.toFixed(2)}</p>
                    </div>
                    <Select value={order.status} onValueChange={(val) => updateOrderStatus(order.id, val)}>
                      <SelectTrigger data-testid={`order-status-${order.id}`} className="w-36 h-8 rounded-none text-xs uppercase tracking-wider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-gray-500 font-['Outfit']">
                    {order.items?.map((item, i) => (
                      <span key={item.name + i}>{item.name} x{item.quantity}{i < order.items.length - 1 ? ", " : ""}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-lg rounded-none border-[#0A0A0A] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-black uppercase tracking-tight font-['Cabinet_Grotesk']">
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Name</label>
              <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Description</label>
              <textarea data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="w-full border border-gray-200 p-2 text-sm h-20 resize-none focus:outline-none focus:border-[#0A0A0A]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Price</label>
                <Input data-testid="product-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="rounded-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Compare Price</label>
                <Input data-testid="product-compare-price-input" type="number" step="0.01" value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: e.target.value })} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Gender</label>
                <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="men">Men</SelectItem>
                    <SelectItem value="women">Women</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Stock</label>
                <Input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Sizes (comma separated)</label>
              <Input data-testid="product-sizes-input" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="rounded-none" placeholder="S,M,L,XL,XXL" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Colors (Name:Hex, comma separated)</label>
              <Input data-testid="product-colors-input" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className="rounded-none" placeholder="Black:#0A0A0A,Grey:#6B7280" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold font-['Outfit'] mb-1 block">Features (one per line)</label>
              <textarea data-testid="product-features-input" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="w-full border border-gray-200 p-2 text-sm h-20 resize-none focus:outline-none focus:border-[#0A0A0A]" placeholder="Quick-dry mesh fabric&#10;Elastic waistband" />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold font-['Outfit'] cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="accent-[#0A0A0A]" />
                Featured
              </label>
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold font-['Outfit'] cursor-pointer">
                <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="accent-[#0A0A0A]" />
                New Drop
              </label>
            </div>
            <button
              data-testid="save-product-btn"
              type="submit"
              disabled={saving}
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#1a1a1a] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editingProduct ? "Update Product" : "Create Product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
